import { Component, Inject, OnInit, EventEmitter, Output, AfterViewInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { KeynuaWidgetEvent, KeynuaWidgetOptions } from '../../../../types/keynua-widget.d';
import { environment } from 'src/environments/environment';
import { LaftService } from '../../../../services/laft.service';

@Component({
  selector: 'app-keynua-widget-modal',
  templateUrl: './laft-keynua-widget.component.html',
  styleUrls: ['./laft-keynua-widget.component.scss'],
})
export class LaftKeynuaWidgetModalComponent implements OnInit, AfterViewInit {
  @Output() formSubmitted = new EventEmitter<any>();
  @Output() closeEvent = new EventEmitter<any>();

  public keynuaToken: string = '';
  public keynuaUrl: string;
  public widgetInitialized: boolean = false;
  private widgetScriptLoaded: boolean = false;
  private widgetCompletedNotified: boolean = false; // Flag to prevent duplicate API calls

  constructor(
    public dialogRef: MatDialogRef<LaftKeynuaWidgetModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private laftService: LaftService
  ) {}

  ngOnInit(): void {
    this.keynuaUrl = this.data?.keynuaUrl || '';
    if (this.keynuaUrl) {
      try {
        const url = new URL(this.keynuaUrl);
        this.keynuaToken = url.searchParams.get('token') || '';
      } catch (error) {
        console.error('Error parsing Keynua URL:', error);
      }
    }
  }

  ngAfterViewInit(): void {
    // Cargar el script del widget y luego inicializar
    this.loadWidgetScript().then(() => {
      if (this.keynuaToken) {
        this.initializeWidget();
      }
    });
  }

  private loadWidgetScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (window.Keynua) {
        this.widgetScriptLoaded = true;
        resolve();
        return;
      }

      if (this.widgetScriptLoaded) {
        const checkInterval = setInterval(() => {
          if (window.Keynua) {
            clearInterval(checkInterval);
            resolve();
          }
        }, 100);
        return;
      }

      let scriptUrl = `${new URL(environment.signingKeynuaUrl).origin}/widgets/widget.js`;
      if (this.keynuaUrl) {
        try {
          const url = new URL(this.keynuaUrl);
          scriptUrl = `${url.origin}/widgets/widget.js`;
        } catch (error) {
          console.error('Error determining widget script URL:', error);
        }
      }

      const script = document.createElement('script');
      script.src = scriptUrl;
      script.async = true;
      script.onload = () => {
        this.widgetScriptLoaded = true;
        resolve();
      };
      script.onerror = () => {
        console.error('Error loading Keynua widget script');
        reject(new Error('Failed to load Keynua widget script'));
      };
      document.head.appendChild(script);
    });
  }

  private initializeWidget(): void {
    if (this.widgetInitialized) {
      return;
    }

    try {
      window.Keynua?.run({
        token: this.keynuaToken,
        widgetContainerId: 'keynua-widget-container',
        uiCallback: (event: KeynuaWidgetEvent) => {
          this.handleKeynuaEvent(event);
        },
        autoRefresh: 30,
      });
      this.widgetInitialized = true;
    } catch (error) {
      console.error('Error initializing Keynua widget:', error);
    }
  }

  private handleKeynuaEvent(event: KeynuaWidgetEvent): void {
    console.log('========================================');
    console.log('🔔 KEYNUA EVENT:', event.name);
    console.log('📋 Full event data:', JSON.stringify(event, null, 2));
    console.log('========================================');

    switch (event.name) {
      case 'SubmitFinished':
        console.log('✅ KEYNUA: User completed the submission process!');
        this.notifyWidgetCompleted();
        break;

      case 'SubmitFailed':
        console.error('❌ KEYNUA: Submit failed:', event);
        break;

      case 'ItemErrorsEvent':
        console.error('⚠️ KEYNUA: Item errors:', event);
        break;

      case 'DetailsFailedEvent':
        console.error('❌ KEYNUA: Details failed:', event);
        break;

      case 'ItemSubmittedEvent':
        // Posible evento cuando un item individual es enviado
        console.log('📤 KEYNUA: Item submitted:', event);
        break;

      case 'ItemCompletedEvent':
        // Posible evento cuando un item se completa
        console.log('✔️ KEYNUA: Item completed:', event);
        break;

      default:
        // Log eventos de UI/carga para referencia
        if (event.name?.includes('Loaded') || event.name?.includes('Displayed')) {
          console.log('📱 KEYNUA UI event:', event.name);
        }
        // Check for any event that contains "Finished" or "Completed" in the name
        if (event.name?.includes('Finished') || event.name?.includes('Completed')) {
          console.log('🎯 KEYNUA: Detected completion event:', event.name);
          this.notifyWidgetCompleted();
        }
        break;
    }
  }

  /**
   * Notify the backend that the user has completed the Keynua widget process
   * This marks keynuaWidgetSigned: true in the LAFT declaration
   */
  private notifyWidgetCompleted(): void {
    if (this.widgetCompletedNotified) {
      console.log('⏭️ KEYNUA: Widget completion already notified, skipping...');
      return;
    }

    this.widgetCompletedNotified = true;
    console.log('📡 KEYNUA: Notifying backend that widget is completed...');

    this.laftService.markKeynuaWidgetCompleted().subscribe({
      next: (response) => {
        console.log('✅ KEYNUA: Backend notified successfully:', response);
        // Close the dialog after a short delay
        setTimeout(() => {
          this.dialogRef.close({ success: true, widgetCompleted: true });
        }, 1500);
      },
      error: (error) => {
        console.error('❌ KEYNUA: Error notifying backend:', error);
        // Still close the dialog but indicate the error
        setTimeout(() => {
          this.dialogRef.close({ success: false, widgetCompleted: true, error: error });
        }, 1500);
      }
    });
  }

  close() {
    if (!this.widgetCompletedNotified) {
      this.laftService.logKeynuaWidgetDropOff().subscribe({
        next: () => {},
        error: (err) => console.warn('Keynua drop-off audit log failed:', err),
      });
    }
    this.dialogRef.close();
    this.closeEvent.emit();
  }
}

